import { useEffect, useRef, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel, StatusPill, EmptyState } from "../../components/Bits";
import StudentAvatar from "../../components/StudentAvatar";
import { api, formatApiError } from "../../lib/api";
import { Plus, Search, Trash2, Pencil, X, Upload, ImageOff } from "lucide-react";
import { toast } from "sonner";

const EMPTY_IMG = "https://static.prod-images.emergentagent.com/jobs/c36a8fad-3db1-43ac-a499-952035e476fa/images/a802d11e4d83b2617fe4ee2dba4be31745fb010b0df2a64b9dc7c99b14c4cb9f.png";

const empty = {
  name: "", email: "", password: "Student@123", mobile: "", parents_mobile: "",
  permanent_address: "", course: "", college: "", age: 18, dob: "", room_id: "", bed_number: "",
  joining_date: "", emergency_contact: "", blood_group: "",
  admission_no: "", aadhar_number: "", monthly_rent: "", security_deposit: "",
  local_guardian_name: "", local_guardian_mobile: "", local_guardian_relation: "", local_guardian_address: "",
};

export default function AdminStudents() {
  const [rows, setRows] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef(null);

  async function load() {
    const [s, r] = await Promise.all([api.get("/admin/students"), api.get("/admin/rooms")]);
    setRows(s.data); setRooms(r.data);
  }
  useEffect(() => { load(); }, []);

  // Beds available in a room (excluding current student if editing)
  function bedsForRoom(roomId) {
    const room = rooms.find((r) => r.id === roomId);
    if (!room) return [];
    return Array.from({ length: room.capacity }, (_, i) => i + 1).map((bn) => {
      const occ = room.occupants.find((o) => o.bed_number === bn);
      const isMine = editing && occ?.id === editing.id;
      return { bedNumber: bn, occupied: !!occ && !isMine, occupant: isMine ? null : occ };
    });
  }

  function resetPhoto() {
    setPhotoFile(null); setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }
  function openCreate() { setEditing(null); setForm(empty); resetPhoto(); setOpen(true); }
  function openEdit(s) { setEditing(s); setForm({ ...empty, ...s }); resetPhoto(); setOpen(true); }

  function onPickPhoto(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
      toast.error("Use a JPG, PNG or WEBP image");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be ≤ 5 MB");
      return;
    }
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  }

  async function uploadPhotoFor(studentId) {
    if (!photoFile) return;
    const fd = new FormData();
    fd.append("file", photoFile);
    await api.post(`/admin/students/${studentId}/photo`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      let targetId;
      if (editing) {
        const payload = { ...form };
        delete payload.password; delete payload.email; delete payload.id; delete payload.user_id;
        delete payload.created_at; delete payload.photo_path; delete payload.photo_type; delete payload.photo_updated_at;
        if (payload.monthly_rent === "") payload.monthly_rent = null;
        else if (payload.monthly_rent != null) payload.monthly_rent = Number(payload.monthly_rent);
        if (payload.security_deposit === "") payload.security_deposit = null;
        else if (payload.security_deposit != null) payload.security_deposit = Number(payload.security_deposit);
        await api.put(`/admin/students/${editing.id}`, payload);
        targetId = editing.id;
      } else {
        const payload = { ...form, age: Number(form.age) };
        payload.monthly_rent = payload.monthly_rent === "" ? null : Number(payload.monthly_rent);
        payload.security_deposit = payload.security_deposit === "" ? null : Number(payload.security_deposit);
        const { data } = await api.post("/admin/students", payload);
        targetId = data.id;
      }
      if (photoFile && targetId) await uploadPhotoFor(targetId);
      toast.success(editing ? "Resident updated" : "Resident added");
      setOpen(false);
      resetPhoto();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function removePhoto() {
    if (!editing) { resetPhoto(); return; }
    if (!window.confirm("Remove this resident's photo?")) return;
    try {
      await api.delete(`/admin/students/${editing.id}/photo`);
      setEditing({ ...editing, photo_path: null, photo_updated_at: null });
      resetPhoto();
      toast.success("Photo removed");
      load();
    } catch { toast.error("Could not remove photo"); }
  }

  async function remove(s) {
    if (!window.confirm(`Remove ${s.name}? This also deletes their login.`)) return;
    await api.delete(`/admin/students/${s.id}`);
    toast.success("Resident removed");
    load();
  }

  const filtered = (rows || []).filter((s) =>
    !q || [s.name, s.email, s.room_number, s.course, s.college, s.mobile]
      .filter(Boolean).join(" ").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <AppShell>
      <PageHeader
        overline="People"
        title="Residents"
        subtitle="Add, edit and manage all Cozy Heaven residents."
        actions={
          <button data-testid="add-student-btn" onClick={openCreate} className="bg-terracotta text-white px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-transform">
            <Plus className="h-4 w-4" /> Add resident
          </button>
        }
      />

      <Panel
        title={`${filtered.length} ${filtered.length === 1 ? "resident" : "residents"}`}
        action={
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-warm" />
            <input
              placeholder="Search by name, room, course…"
              value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="students-search-input"
              className="pl-8 pr-3 py-2 rounded-md border border-warm bg-white text-sm w-72 focus:outline-none focus:ring-2 focus:ring-[#E8C2B8]"
            />
          </div>
        }
      >
        {rows === null ? (
          <div className="text-sm text-warm">Loading…</div>
        ) : filtered.length === 0 ? (
          <EmptyState title="No residents yet" hint="Add the first resident to get started." image={EMPTY_IMG} />
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="min-w-full text-sm" data-testid="students-table">
              <thead>
                <tr className="bg-[#F4F1EA] text-warm uppercase tracking-wider text-xs">
                  <th className="text-left px-6 py-3">Name</th>
                  <th className="text-left px-6 py-3">Room</th>
                  <th className="text-left px-6 py-3">Course / College</th>
                  <th className="text-left px-6 py-3">Mobile</th>
                  <th className="text-left px-6 py-3">Parent</th>
                  <th className="text-left px-6 py-3">Status</th>
                  <th className="text-right px-6 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="border-t border-warm hover:bg-[#FDFBF7]" data-testid={`student-row-${s.id}`}>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <StudentAvatar student={s} size={36} />
                        <div>
                          <div className="font-medium text-deep">{s.name}</div>
                          <div className="text-xs text-warm">{s.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      {s.room_number ? (
                        <div>
                          <div className="text-deep">Room {s.room_number}</div>
                          {s.bed_number && <div className="text-xs text-warm">Bed {s.bed_number}</div>}
                        </div>
                      ) : <span className="text-warm">—</span>}
                    </td>
                    <td className="px-6 py-3">
                      <div>{s.course || "—"}</div>
                      <div className="text-xs text-warm">{s.college || ""}</div>
                    </td>
                    <td className="px-6 py-3">{s.mobile}</td>
                    <td className="px-6 py-3">{s.parents_mobile}</td>
                    <td className="px-6 py-3"><StatusPill status={s.status || "active"} /></td>
                    <td className="px-6 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(s)} className="p-2 hover:bg-[#F4F1EA] rounded-md" data-testid={`edit-student-${s.id}`}>
                        <Pencil className="h-4 w-4 text-warm" />
                      </button>
                      <button onClick={() => remove(s)} className="p-2 hover:bg-[#F5D8D8] rounded-md" data-testid={`delete-student-${s.id}`}>
                        <Trash2 className="h-4 w-4 text-[#8C3F3F]" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {open && (
        <Modal title={editing ? "Edit resident" : "Add resident"} onClose={() => setOpen(false)}>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="student-form">
            {/* Photo uploader spans full width */}
            <div className="sm:col-span-2 flex items-center gap-5 p-4 rounded-md border border-dashed border-warm bg-[#FDFBF7]">
              <div className="shrink-0">
                {photoPreview ? (
                  <img src={photoPreview} alt="" className="h-20 w-20 rounded-full object-cover border border-warm" data-testid="photo-preview" />
                ) : editing ? (
                  <StudentAvatar student={editing} size={80} />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-[#F4F1EA] flex items-center justify-center text-warm">
                    <ImageOff className="h-6 w-6" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-deep">Profile photo</div>
                <div className="text-xs text-warm mt-0.5">JPG, PNG or WEBP · up to 5 MB</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-warm text-sm cursor-pointer hover:bg-white" data-testid="upload-photo-btn">
                    <Upload className="h-4 w-4" />
                    {photoFile ? "Choose another" : "Choose photo"}
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPickPhoto} className="hidden" />
                  </label>
                  {(photoFile || editing?.photo_path) && (
                    <button type="button" onClick={removePhoto} className="text-sm px-3 py-1.5 rounded-md text-[#8C3F3F] hover:bg-[#F5D8D8]" data-testid="remove-photo-btn">
                      {photoFile ? "Clear selection" : "Remove photo"}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <Field label="Full name *"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={input} data-testid="form-name" /></Field>
            <Field label="Email *"><input type="email" disabled={!!editing} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={input} data-testid="form-email" /></Field>
            {!editing && (
              <Field label="Default password"><input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={input} data-testid="form-password" /></Field>
            )}
            <Field label="Mobile *"><input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} className={input} /></Field>
            <Field label="Parents mobile *"><input required value={form.parents_mobile} onChange={(e) => setForm({ ...form, parents_mobile: e.target.value })} className={input} /></Field>
            <Field label="DOB *"><input type="date" required value={form.dob || ""} onChange={(e) => setForm({ ...form, dob: e.target.value })} className={input} /></Field>
            <Field label="Age *"><input type="number" required value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className={input} /></Field>
            <Field label="Course *"><input required value={form.course} onChange={(e) => setForm({ ...form, course: e.target.value })} className={input} /></Field>
            <Field label="College *"><input required value={form.college} onChange={(e) => setForm({ ...form, college: e.target.value })} className={input} /></Field>
            <Field label="Room">
              <select value={form.room_id || ""} onChange={(e) => setForm({ ...form, room_id: e.target.value, bed_number: "" })} className={input} data-testid="form-room">
                <option value="">— Unassigned —</option>
                {rooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    Room {r.number} · {r.category?.name || "—"} · {r.free_beds + (editing && editing.room_id === r.id ? 1 : 0)}/{r.capacity} free
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Bed">
              <select value={form.bed_number || ""} onChange={(e) => setForm({ ...form, bed_number: e.target.value ? Number(e.target.value) : "" })} className={input} disabled={!form.room_id} data-testid="form-bed">
                <option value="">— Select bed —</option>
                {bedsForRoom(form.room_id).map((b) => (
                  <option key={b.bedNumber} value={b.bedNumber} disabled={b.occupied}>
                    Bed {b.bedNumber}{b.occupied ? ` — taken by ${b.occupant?.name || "someone"}` : " — available"}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Joining date"><input type="date" value={form.joining_date || ""} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} className={input} /></Field>
            <Field label="Blood group"><input value={form.blood_group || ""} onChange={(e) => setForm({ ...form, blood_group: e.target.value })} className={input} placeholder="e.g. O+" /></Field>
            <Field label="Emergency contact"><input value={form.emergency_contact || ""} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} className={input} /></Field>
            <Field label="Admission no."><input value={form.admission_no || ""} onChange={(e) => setForm({ ...form, admission_no: e.target.value })} className={input} /></Field>
            <Field label="Aadhaar number"><input value={form.aadhar_number || ""} onChange={(e) => setForm({ ...form, aadhar_number: e.target.value })} className={input} placeholder="12-digit Aadhaar" /></Field>
            <Field label="Monthly rent (₹)"><input type="number" min="0" step="1" value={form.monthly_rent ?? ""} onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })} className={input} /></Field>
            <Field label="Security deposit (₹)"><input type="number" min="0" step="1" value={form.security_deposit ?? ""} onChange={(e) => setForm({ ...form, security_deposit: e.target.value })} className={input} /></Field>
            <div className="sm:col-span-2 mt-2 pt-3 border-t border-warm">
              <div className="text-xs font-semibold uppercase tracking-wider text-warm mb-3">Local guardian</div>
            </div>
            <Field label="Guardian name"><input value={form.local_guardian_name || ""} onChange={(e) => setForm({ ...form, local_guardian_name: e.target.value })} className={input} /></Field>
            <Field label="Guardian mobile"><input value={form.local_guardian_mobile || ""} onChange={(e) => setForm({ ...form, local_guardian_mobile: e.target.value })} className={input} /></Field>
            <Field label="Relation"><input value={form.local_guardian_relation || ""} onChange={(e) => setForm({ ...form, local_guardian_relation: e.target.value })} className={input} placeholder="e.g. Uncle" /></Field>
            <Field label="Guardian address"><input value={form.local_guardian_address || ""} onChange={(e) => setForm({ ...form, local_guardian_address: e.target.value })} className={input} /></Field>
            <div className="sm:col-span-2">
              <Field label="Permanent address *">
                <textarea required rows={2} value={form.permanent_address} onChange={(e) => setForm({ ...form, permanent_address: e.target.value })} className={input} />
              </Field>
            </div>

            <div className="sm:col-span-2 flex justify-end gap-2 mt-2">
              <button type="button" onClick={() => setOpen(false)} className="px-5 py-2 rounded-md text-sm border border-warm text-deep hover:bg-[#F4F1EA]">Cancel</button>
              <button disabled={saving} type="submit" data-testid="save-student-btn" className="bg-terracotta text-white px-6 py-2 rounded-md text-sm disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
            </div>
          </form>
        </Modal>
      )}
    </AppShell>
  );
}

const input = "w-full px-3 py-2 rounded-md border border-warm bg-white focus:outline-none focus:ring-2 focus:ring-[#E8C2B8] focus:border-[#C86F58] text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-deep mb-1.5 block uppercase tracking-wider">{label}</span>
      {children}
    </label>
  );
}

export function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4 animate-fade-up" onClick={onClose}>
      <div className={`bg-white rounded-lg border border-warm w-full ${wide ? "max-w-3xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-warm sticky top-0 bg-white">
          <h3 className="font-heading text-2xl text-deep">{title}</h3>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F4F1EA] rounded-md" data-testid="modal-close-btn"><X className="h-4 w-4" /></button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}
